import { ReactNode, useState } from 'react';
import { Button, Divider, Grid, Tag } from 'antd';
import { CloudDownloadOutlined, PrinterOutlined, SyncOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { getPaymentDetailsOrderApi, getPaymentDetailsOrderToPrintApi } from '@http/payments';
import {
  cancelExchangeApi,
  cancelTransactionApi,
  electricTransactionApproveApi,
  proceedExchangeApi,
  transactionApproveApi,
} from '@http/transactions';
import {
  repeatAsycudaTransactionAC,
  repeatElectricTransactionAC,
  repeatExchangeTransactionAC,
  repeatInterbankTransactionAC,
  repeatInternalTransactionAC,
  repeatOwnTransactionAC,
  repeatSwiftRubTransactionAC,
  repeatSwiftTjsTransactionAC,
  repeatSwiftTransactionAC,
} from '@store/reducers/transactions';
import digitNumber from '@utils/digitNumber';
import getBankName from '@utils/bankName';
import TransactionActionModal from './TransactionActionModal';
import { DISPOSAL_ACCOUNT } from './constants';
import { Transaction, TransactionAction } from './types';

const REPEAT_ACTIONS = {
  OWN: repeatOwnTransactionAC,
  INTERNAL: repeatInternalTransactionAC,
  INTERBANK: repeatInterbankTransactionAC,
  SWIFT: repeatSwiftTransactionAC,
  SWIFT_RUB: repeatSwiftRubTransactionAC,
  SWIFT_TJS: repeatSwiftTjsTransactionAC,
  EXCHANGE: repeatExchangeTransactionAC,
  ELECTRIC: repeatElectricTransactionAC,
  ASYCUDA: repeatAsycudaTransactionAC,
} as const;

const getRetryPath = (item: Transaction) => {
  if (item.type === 'ASYCUDA') {
    return item.receiving_ac === DISPOSAL_ACCOUNT ? '/payments/disposal' : '/payments/asycuda';
  }
  return `/payments/${item.type.toLowerCase()}`;
};

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className='grid grid-cols-6'>
      <p className='col-span-2'>{label}: </p>
      <div className='ml-3 font-semibold col-span-4'>{children}</div>
    </div>
  );
}

interface TransactionInfoProps {
  item: Transaction;
  onReload: () => void;
}

function TransactionInfo({ item, onReload }: TransactionInfoProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const screens = Grid.useBreakpoint();
  const user = useAppSelector(state => state.appReducer.user);

  const [modalAction, setModalAction] = useState<TransactionAction | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const buttonSize = screens.md ? 'middle' : 'small';
  const isExchangeInReview = item.type === 'EXCHANGE' && ['IN_BANK_REVIEW', 'BANK_APPROVED'].includes(item.status);

  const retryPayment = () => {
    navigate(getRetryPath(item));
    const repeatAction = REPEAT_ACTIONS[item.type as keyof typeof REPEAT_ACTIONS];
    if (repeatAction) {
      dispatch(repeatAction(item));
    }
  };

  const submitAction = async () => {
    if (!modalAction || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    const params = { trans_id: item.trans_id, action: modalAction };
    const isSuccess =
      item.type === 'ELECTRIC'
        ? await dispatch(electricTransactionApproveApi(params))
        : await dispatch(transactionApproveApi({ params }));
    setIsSubmitting(false);

    if (isSuccess) {
      setModalAction(null);
    }
    onReload();
  };

  const runAndReload = async (request: Promise<boolean>) => {
    if (await request) {
      onReload();
    }
  };

  return (
    <div className='bg-slate-50'>
      <div className='grid grid-cols-7 items-center py-5 max-sm:px-8 px-2 border-b lg:gap-5 max-sm:text-xs'>
        <div />
        <div className='max-sm:col-span-7 max-sm:leading-6 col-span-5 leading-8'>
          <DetailRow label={t('Transaction type')}>{item.type_name}</DetailRow>
          <DetailRow label={t('TxID')}>{item.trans_id}</DetailRow>
          <DetailRow label={t('Sender account')}>
            {item.sending_ac} ({item.sending_ccy}) {item.sending_ac_name !== '' ? '- ' + item.sending_ac_name : null}
          </DetailRow>
          {item.type !== 'ELECTRIC' && (
            <DetailRow label={t('Recipient account')}>
              {item.receiving_ac} ({item.receiving_ccy})
              {item.receiver_ac_name !== '' ? '- ' + item.receiver_ac_name : null}
            </DetailRow>
          )}
          {item.receiver_name && <DetailRow label={t('Recipient')}>{item.receiver_name}</DetailRow>}
          {item.tax_id && <DetailRow label={t('Recipient TIN')}>{item.tax_id}</DetailRow>}
          {item.doc_type_title && (
            <DetailRow label={t('Document type')}>
              ({item.doc_type}) {item.doc_type_title}
            </DetailRow>
          )}
          {item.doc_no && <DetailRow label={t('Document number')}>{item.doc_no}</DetailRow>}
          {item.type === 'INTERBANK' && (
            <DetailRow label={t('Beneficiary Bank')}>{getBankName(item.bank_code)}</DetailRow>
          )}
          {(item.type === 'SWIFT' || item.type === 'SWIFT_RUB') && (
            <>
              <DetailRow label={t('Recipient country')}>{item.receiver_country}</DetailRow>
              <DetailRow label={t('Beneficiary Bank')}>{item.receiver_bank_name}</DetailRow>
              <DetailRow label={t('Recipient bank address')}>{item.receiver_bank_address}</DetailRow>
              <DetailRow label={t('Recipient address')}>{item.receiver_address}</DetailRow>
            </>
          )}
          {item.type === 'EXCHANGE' ? (
            <>
              <DetailRow label={t('Credit amount')}>
                {digitNumber(item.sending_amount)} {item.sending_ccy}
              </DetailRow>
              <DetailRow label={t('Rate')}>{item.xrate}</DetailRow>
              <DetailRow label={t('Debit amount')}>
                {digitNumber(item.receiving_amount)} {item.receiving_ccy}
              </DetailRow>
            </>
          ) : (
            <DetailRow label={t('Amount')}>
              {digitNumber(item.sending_amount)} {item.sending_ccy}
            </DetailRow>
          )}
          {item.type === 'ELECTRIC' && (
            <>
              <DetailRow label={t('Personal account number')}>{item.utilpay_account}</DetailRow>
              <DetailRow label={t('Recipient')}>{item.utilpay_name}</DetailRow>
              <DetailRow label={t('Address')}>{item.utilpay_address}</DetailRow>
            </>
          )}
          {item.fee_amount ? (
            <DetailRow label={t('Commission')}>
              {digitNumber(item.fee_amount)} {item.sending_ccy}
            </DetailRow>
          ) : null}
          <DetailRow label={t('Narrative')}>{item.narrative}</DetailRow>
          <DetailRow label={t('Initiator')}>{item.initiator_name}</DetailRow>
          <DetailRow label={t('Date of creation')}>{item.created_at.slice(0, 19)}</DetailRow>
          {item.authenticator.length > 0 && (
            <DetailRow label={t('Authorizers')}>
              {item.authenticator.map(authenticator => (
                <div key={authenticator.authenticator_name} className='flex items-center'>
                  <p className='mr-3'>{authenticator.authenticator_name}</p>
                  <span className='font-normal'>
                    {authenticator.authed === true && <Tag color='success'>{t('Approved')}</Tag>}
                    {authenticator.authed === false && <Tag color='error'>{t('Rejected')}</Tag>}
                    {authenticator.authed === null && <Tag color='processing'>{t('Signing in')}</Tag>}
                  </span>
                </div>
              ))}
            </DetailRow>
          )}

          {(item.status === 'SUCCESS' || item.status === 'IN_PROGRESS') && (
            <>
              <Divider />
              <div className='flex gap-4'>
                <Button icon={<SyncOutlined />} size={buttonSize} type='primary' onClick={retryPayment}>
                  {t('Retry payment')}
                </Button>
                <Button
                  icon={<CloudDownloadOutlined />}
                  size={buttonSize}
                  type='primary'
                  onClick={() => dispatch(getPaymentDetailsOrderApi({ trans_id: item.trans_id }))}
                >
                  {t('Download PDF')}
                </Button>
                {screens.md && (
                  <Button
                    icon={<PrinterOutlined />}
                    type='primary'
                    onClick={() => dispatch(getPaymentDetailsOrderToPrintApi({ trans_id: item.trans_id }))}
                  >
                    {t('Print')}
                  </Button>
                )}
              </div>
            </>
          )}

          {item.is_authenticator && item.status === 'TO_APPROVE' && (
            <>
              <Divider />
              <div className='flex gap-4'>
                <Button type='primary' danger onClick={() => setModalAction('DECLINE')}>
                  {t('Reject')}
                </Button>
                <Button type='primary' onClick={() => setModalAction('APPROVE')}>
                  {t('Confirm')}
                </Button>
              </div>
            </>
          )}

          {item.status === 'IN_APPROVAL' && (
            <>
              <Divider />
              <Button danger onClick={() => runAndReload(dispatch(cancelTransactionApi({ trans_id: item.trans_id })))}>
                {t('Cancel')}
              </Button>
            </>
          )}

          {isExchangeInReview && item.initiator_id === user.id && (
            <>
              <Divider />
              <div className='flex gap-4'>
                <Button
                  type='primary'
                  disabled={item.status !== 'BANK_APPROVED'}
                  onClick={() => runAndReload(dispatch(proceedExchangeApi({ trans_id: item.trans_id })))}
                >
                  {t('Confirm')}
                </Button>
                <Button danger onClick={() => runAndReload(dispatch(cancelExchangeApi({ trans_id: item.trans_id })))}>
                  {t('Cancel')}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <TransactionActionModal
        action={modalAction}
        isSubmitting={isSubmitting}
        onConfirm={submitAction}
        onCancel={() => setModalAction(null)}
      />
    </div>
  );
}

export default TransactionInfo;
